/**
 * The expert review desk.
 *
 * A reviewer's job is a loop: read the item, judge it, act, move on. Published
 * measurements put a competent reviewer around three minutes per item, so a fifty-item
 * batch is most of an afternoon and every extra click is multiplied fifty times. The
 * layout is built around that loop rather than around the data model: queue on the left,
 * the item in the middle at reading width, the decision pinned to the bottom.
 *
 * Time per item is measured and sent with each decision. It is never shown as a
 * countdown; a reviewer racing a clock makes worse calls, and the number exists to tell
 * us what review actually costs, not to push anyone.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import {
  ItemDetail, ItemEditPayload, QueueItem, ReviewActionName, ReviewStats, ReviewerContext,
  REASON_LABELS,
} from '../../inbde/reviewTypes';
import * as reviewApi from '../../inbde/reviewApi';
import ReviewQueue from '../../components/inbde/review/ReviewQueue';
import ItemCard from '../../components/inbde/review/ItemCard';
import ItemEditor from '../../components/inbde/review/ItemEditor';
import DecisionBar from '../../components/inbde/review/DecisionBar';
import ConceptDesk from '../../components/inbde/review/ConceptDesk';
import { Eyebrow, ItemSkeleton, ErrorNote } from '../../components/inbde/review/primitives';

const FONT_LINKS = [
  'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500;600&display=swap',
];

function errorText(e: any, fallback: string): string {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.message) return [d.message, ...(d.problems || d.concept_ids || [])].join(' ');
  return e?.message || fallback;
}

const ReviewDesk: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  const [exams, setExams] = useState<string[] | null>(null);
  const [exam, setExam] = useState<string | null>(null);
  const [context, setContext] = useState<ReviewerContext | null>(null);

  const [status, setStatus] = useState('ai_unreviewed');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [item, setItem] = useState<ItemDetail | null>(null);
  const [itemLoading, setItemLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<ReviewStats | null>(null);
  const [showStats, setShowStats] = useState(false);
  // Two things get reviewed here, in this order: the vocabulary every item is written
  // against, then the items themselves.
  const [tab, setTab] = useState<'concepts' | 'items'>('items');

  const openedAt = useRef<number>(Date.now());
  const [elapsed, setElapsed] = useState(0);

  // IBM Plex is the INBDE brand face. Injected on mount and removed on unmount so the
  // rest of the app never pays for it (the pattern used by pages/Paathshaala.tsx).
  useEffect(() => {
    const prevTitle = document.title;
    const prevBrand = document.documentElement.dataset.brand;
    document.title = 'Review desk';
    document.documentElement.dataset.brand = 'inbde';
    const links = FONT_LINKS.map((href) => {
      const el = document.createElement('link');
      el.rel = 'stylesheet';
      el.href = href;
      document.head.appendChild(el);
      return el;
    });
    return () => {
      document.title = prevTitle;
      if (prevBrand) document.documentElement.dataset.brand = prevBrand;
      else delete document.documentElement.dataset.brand;
      links.forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    };
  }, []);

  useEffect(() => {
    if (!authLoading && !user) navigate('/login', { replace: true });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    reviewApi.myExams()
      .then((list) => { if (!cancelled) { setExams(list); setExam(list[0] ?? null); } })
      .catch((e) => { if (!cancelled) { setExams([]); setError(errorText(e, 'Could not load your reviewer access.')); } });
    return () => { cancelled = true; };
  }, [user]);

  const loadContext = useCallback(async (key: string) => {
    try { setContext(await reviewApi.getContext(key)); }
    catch (e) { setError(errorText(e, 'Could not load the desk.')); }
  }, []);

  useEffect(() => { if (exam) loadContext(exam); }, [exam, loadContext]);

  const loadQueue = useCallback(async (key: string) => {
    setQueueLoading(true);
    try {
      const rows = await reviewApi.getQueue(key, { status, flaggedOnly, search });
      setQueue(rows);
      setSelectedId((prev) => (prev && rows.some((r) => r.id === prev) ? prev : rows[0]?.id ?? null));
    } catch (e) {
      setError(errorText(e, 'Could not load the queue.'));
    } finally {
      setQueueLoading(false);
    }
  }, [status, flaggedOnly, search]);

  useEffect(() => {
    if (!exam) return;
    const t = setTimeout(() => loadQueue(exam), search ? 250 : 0);   // debounce typing only
    return () => clearTimeout(t);
  }, [exam, loadQueue, search]);

  useEffect(() => {
    if (!exam || !selectedId) { setItem(null); return; }
    let cancelled = false;
    setItemLoading(true);
    setEditing(false);
    setError(null);
    reviewApi.getItem(exam, selectedId)
      .then((detail) => { if (!cancelled) { setItem(detail); openedAt.current = Date.now(); setElapsed(0); } })
      .catch((e) => { if (!cancelled) setError(errorText(e, 'Could not load that item.')); })
      .finally(() => { if (!cancelled) setItemLoading(false); });
    return () => { cancelled = true; };
  }, [exam, selectedId]);

  useEffect(() => {
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - openedAt.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, []);

  const index = useMemo(() => queue.findIndex((q) => q.id === selectedId), [queue, selectedId]);

  const step = useCallback((delta: number) => {
    if (queue.length === 0) return;
    const next = Math.min(queue.length - 1, Math.max(0, (index < 0 ? 0 : index) + delta));
    setSelectedId(queue[next].id);
  }, [queue, index]);

  const runAction = useCallback(async (
    action: ReviewActionName, extra?: { reason_code?: string; notes?: string },
  ) => {
    if (!exam || !item) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await reviewApi.act(exam, item.id, {
        action, ...extra, seconds_spent: Math.floor((Date.now() - openedAt.current) / 1000),
      });
      setItem(updated);
      showToast('success',
        action === 'approve' ? 'Approved' :
        action === 'reject' ? 'Rejected' :
        action === 'flag' ? 'Flagged for discussion' :
        action === 'unflag' ? 'Flag cleared' : 'Reopened',
      );
      await Promise.all([loadContext(exam), loadQueue(exam)]);
      if (action === 'approve' || action === 'reject') step(1);
    } catch (e) {
      setError(errorText(e, 'That action did not go through.'));
    } finally {
      setBusy(false);
    }
  }, [exam, item, showToast, loadContext, loadQueue, step]);

  const runEdit = useCallback(async (payload: ItemEditPayload) => {
    if (!exam || !item) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await reviewApi.editItem(exam, item.id, {
        ...payload, seconds_spent: Math.floor((Date.now() - openedAt.current) / 1000),
      });
      setItem(updated);
      setEditing(false);
      showToast('success', 'Changes saved');
      loadQueue(exam);
    } catch (e) {
      setError(errorText(e, 'Could not save those changes.'));
    } finally {
      setBusy(false);
    }
  }, [exam, item, showToast, loadQueue]);

  // Keyboard path. Ignored while typing, so a reviewer writing a rejection note never
  // triggers an action by pressing a letter.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (typing || e.metaKey || e.ctrlKey || e.altKey || busy || editing || !item || tab !== 'items') return;
      const k = e.key.toLowerCase();
      if (k === 'j') { e.preventDefault(); step(1); }
      else if (k === 'k') { e.preventDefault(); step(-1); }
      else if (k === 'a') { e.preventDefault(); runAction('approve'); }
      else if (k === 'f') { e.preventDefault(); runAction(item.flagged ? 'unflag' : 'flag'); }
      else if (k === 'e') { e.preventDefault(); setEditing(true); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [busy, editing, item, step, runAction, tab]);

  const openStats = async () => {
    if (!exam) return;
    setShowStats(true);
    try { setStats(await reviewApi.getStats(exam)); }
    catch (e) { setError(errorText(e, 'Could not load the review summary.')); }
  };

  if (authLoading || exams === null) {
    return (
      <div className="in-root min-h-[100dvh] flex items-center justify-center">
        <span className="in-eyebrow">Loading</span>
      </div>
    );
  }

  if (exams.length === 0) {
    return (
      <div className="in-root min-h-[100dvh] flex items-center justify-center px-6">
        <div className="in-surface max-w-[46ch] p-8 flex flex-col gap-3">
          <Eyebrow>Review desk</Eyebrow>
          <h1 className="text-[19px] font-semibold" style={{ color: 'var(--text-primary)' }}>
            This account is not on a reviewer roster
          </h1>
          <p className="text-[13.5px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
            The desk is open to clinicians who have been added as reviewers for an exam. If you
            were expecting access, ask whoever invited you to add this email address.
          </p>
          <p className="in-mono text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{user?.email}</p>
        </div>
      </div>
    );
  }

  // A definite height, not a minimum: the shell itself must never scroll, so that the
  // queue and the item pane scroll independently and the decision bar stays reachable
  // no matter how long the item is.
  return (
    <div className="in-root flex flex-col" style={{ height: '100dvh', overflow: 'hidden' }}>
      <header
        className="flex-none flex items-center gap-4 px-5"
        style={{ height: 60, background: 'var(--bg-secondary)', borderBottom: '1px solid var(--in-line)' }}
      >
        <div className="flex items-baseline gap-3">
          <span className="text-[15px] font-semibold" style={{ color: 'var(--text-primary)' }}>Review desk</span>
          {context && (
            <span className="in-mono text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{context.exam_label}</span>
          )}
        </div>

        {exams.length > 1 && (
          <select
            className="in-input in-focus"
            style={{ width: 'auto', padding: '6px 10px', fontSize: 12.5 }}
            value={exam ?? ''}
            onChange={(e) => setExam(e.target.value)}
            aria-label="Exam to review"
          >
            {exams.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        )}

        <nav className="flex items-center gap-1 ml-2" aria-label="What to review">
          {([
            { key: 'concepts' as const, label: 'Vocabulary' },
            { key: 'items' as const, label: 'Items' },
          ]).map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                aria-current={active ? 'page' : undefined}
                className="in-focus text-[12.5px] font-medium px-3 py-1.5"
                style={{
                  borderRadius: 'var(--in-r-control)',
                  border: `1px solid ${active ? 'var(--accent-primary)' : 'transparent'}`,
                  background: active ? 'var(--in-accent-50)' : 'transparent',
                  color: active ? 'var(--accent-secondary)' : 'var(--text-secondary)',
                }}
              >
                {t.label}
                {t.key === 'items' && context && (
                  <span className="in-mono ml-1.5" style={{ opacity: 0.7 }}>
                    {context.counts.ai_unreviewed ?? 0}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-4">
          {context && (
            <span className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
              {context.reviewed_by_me_today} decided today
            </span>
          )}
          {tab === 'items' && (
            <button type="button" className="in-btn in-focus" onClick={openStats}>
              <ChartBarIcon className="w-4 h-4" aria-hidden="true" />
              Batch summary
            </button>
          )}
          {context?.display_credential && (
            <span className="in-mono text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
              {context.display_credential}
            </span>
          )}
        </div>
      </header>

      {tab === 'concepts' && exam && (
        <div className="flex-1 min-h-0 overflow-y-auto" style={{ background: 'var(--bg-primary)' }}>
          <ConceptDesk exam={exam} onChanged={() => loadContext(exam)} />
        </div>
      )}

      <div
        className="flex-1 min-h-0 grid"
        style={{ gridTemplateColumns: 'minmax(240px, 320px) 1fr', display: tab === 'items' ? 'grid' : 'none' }}
      >
        <ReviewQueue
          items={queue}
          loading={queueLoading}
          selectedId={selectedId}
          status={status}
          flaggedOnly={flaggedOnly}
          search={search}
          counts={context?.counts ?? {}}
          onSelect={setSelectedId}
          onStatus={setStatus}
          onFlaggedOnly={setFlaggedOnly}
          onSearch={setSearch}
        />

        <main className="flex flex-col min-h-0" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="mx-auto w-full px-8 py-7 flex flex-col gap-5" style={{ maxWidth: 860 }}>
              {error && <ErrorNote message={error} onRetry={() => exam && loadQueue(exam)} />}

              {itemLoading && <ItemSkeleton />}

              {!itemLoading && !item && !error && (
                <div className="flex flex-col gap-2 py-16 text-center">
                  <span className="text-[15px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Nothing selected
                  </span>
                  <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                    Pick an item from the queue to start reviewing.
                  </span>
                </div>
              )}

              {!itemLoading && item && context && (
                editing ? (
                  <ItemEditor
                    item={item}
                    saving={busy}
                    error={null}
                    onCancel={() => setEditing(false)}
                    onSave={runEdit}
                  />
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <Eyebrow>
                        Item {index >= 0 ? index + 1 : 1} of {queue.length}
                      </Eyebrow>
                      <span className="in-mono text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                        {item.id.slice(0, 8)}
                      </span>
                    </div>
                    <ItemCard item={item} axisALabel={context.axis_a_label} axisBLabel={context.axis_b_label} />
                    {item.history.length > 0 && (
                      <details className="pt-4" style={{ borderTop: '1px solid var(--in-line)' }}>
                        <summary className="in-eyebrow cursor-pointer in-focus">
                          History ({item.history.length})
                        </summary>
                        <ul className="flex flex-col gap-2.5 mt-3">
                          {item.history.map((h) => (
                            <li key={h.id} className="flex gap-3 text-[12.5px]">
                              <span className="in-mono flex-none" style={{ color: 'var(--text-tertiary)', width: 78 }}>
                                {new Date(h.created_at).toLocaleDateString()}
                              </span>
                              <span className="flex-1" style={{ color: 'var(--text-secondary)' }}>
                                <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{h.action}</strong>
                                {h.reviewer_name ? ` by ${h.reviewer_name}` : ''}
                                {h.reason_code ? ` · ${h.reason_code}` : ''}
                                {h.notes ? `. ${h.notes}` : ''}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </>
                )
              )}
            </div>
          </div>

          {item && context && !editing && (
            <DecisionBar
              item={item}
              reasonCodes={context.reason_codes}
              busy={busy}
              elapsedSeconds={elapsed}
              onAct={runAction}
              onEdit={() => setEditing(true)}
            />
          )}
        </main>
      </div>

      <AnimatePresence>
        {showStats && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center px-6 z-50"
            style={{ background: 'rgba(22, 34, 43, 0.45)' }}
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => setShowStats(false)}
            role="dialog"
            aria-modal="true"
            aria-label="Batch summary"
          >
            <motion.div
              className="in-surface w-full p-7 flex flex-col gap-5"
              style={{ maxWidth: 520 }}
              initial={reduce ? false : { opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.2, ease: [0.33, 1, 0.68, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div>
                <Eyebrow>Batch summary</Eyebrow>
                <h2 className="text-[17px] font-semibold mt-1.5" style={{ color: 'var(--text-primary)' }}>
                  What this batch is teaching us
                </h2>
                <p className="text-[12.5px] leading-relaxed mt-1.5" style={{ color: 'var(--text-secondary)' }}>
                  Read this before generating more. Items fail in patterns, and the reasons below
                  say which part of the generator to fix.
                </p>
              </div>

              {!stats && <div className="in-skeleton" style={{ height: 120 }} />}

              {stats && (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Approved', value: stats.approved, color: 'var(--in-correct)' },
                      { label: 'Rejected', value: stats.rejected, color: 'var(--in-incorrect)' },
                      { label: 'Edited', value: stats.edited, color: 'var(--accent-primary)' },
                    ].map((s) => (
                      <div key={s.label} className="flex flex-col gap-1">
                        <span className="in-mono text-[22px] font-semibold" style={{ color: s.color }}>{s.value}</span>
                        <span className="in-eyebrow">{s.label}</span>
                      </div>
                    ))}
                  </div>

                  <dl className="grid gap-x-5 gap-y-2 text-[12.5px]" style={{ gridTemplateColumns: 'max-content 1fr' }}>
                    <dt style={{ color: 'var(--text-tertiary)' }}>Approval rate</dt>
                    <dd className="in-mono" style={{ color: 'var(--text-primary)' }}>
                      {stats.approval_rate === null ? 'Not enough decisions yet' : `${Math.round(stats.approval_rate * 100)}%`}
                    </dd>
                    <dt style={{ color: 'var(--text-tertiary)' }}>Median time per action</dt>
                    <dd className="in-mono" style={{ color: 'var(--text-primary)' }}>
                      {stats.median_seconds === null ? 'Not measured yet' : `${stats.median_seconds}s`}
                    </dd>
                  </dl>

                  {stats.by_reason.length === 0 ? (
                    <p className="text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>
                      No rejections recorded yet, so there is no failure pattern to read.
                    </p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <Eyebrow>Why items were rejected</Eyebrow>
                      {stats.by_reason.map((r) => (
                        <div key={r.reason_code} className="flex items-center gap-3 text-[12.5px]">
                          <span className="flex-1" style={{ color: 'var(--text-primary)' }}>
                            {REASON_LABELS[r.reason_code] || r.reason_code}
                          </span>
                          <span className="in-mono" style={{ color: 'var(--text-secondary)' }}>{r.count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              <button type="button" className="in-btn in-focus self-start" onClick={() => setShowStats(false)}>
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReviewDesk;
